import { ChevronDown, LogOut } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useLocation } from "react-router";

import { useSession, useSignOut } from "../../api/auth";
import { Button } from "../../ui/Button";
import { useToast } from "../../ui/Overlays";
import styles from "./Auth.module.css";

/**
 * Who is signed in, and Sign out, at the end of the bar across the top (the phone menu shows
 * the name too). A session lasts until the user signs out here. Not shown where sign-in is
 * turned off.
 */
export function AccountMenu() {
  const session = useSession();
  const signOut = useSignOut();
  const toast = useToast();
  const location = useLocation();
  const panelId = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [openPath, setOpenPath] = useState(location.pathname);
  if (openPath !== location.pathname) {
    setOpenPath(location.pathname);
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const user = session.data?.status === "signed-in" ? session.data.session : null;
  if (!user?.auth_enabled || !user.username) return null;
  const name = user.username;

  return (
    <div ref={root} className={styles.account}>
      <button
        ref={button}
        type="button"
        className={styles.accountButton}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`Account: ${name}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.avatar} aria-hidden="true">
          {name.charAt(0).toUpperCase()}
        </span>
        <span className={styles.accountName} aria-hidden="true">
          {name}
        </span>
        <ChevronDown size={14} aria-hidden="true" className={styles.chevron} />
      </button>
      <div id={panelId} role="group" aria-label="Account" className={styles.accountPanel} hidden={!open}>
        <p className={styles.signedInAs}>
          Signed in as
          <strong className={styles.signedInName}>{name}</strong>
        </p>
        <Button
          variant="ghost"
          size="sm"
          icon={<LogOut size={16} />}
          loading={signOut.isPending}
          className={styles.signOut}
          onClick={() =>
            signOut.mutate(undefined, {
              onError: () =>
                toast.show({
                  tone: "danger",
                  title: "Couldn't sign out",
                  description: "Check your connection and try again. You are still signed in.",
                }),
            })
          }
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}
